// src/shell/ErrorBoundary.jsx
// Emergency apps cannot white-screen.
// Three boundary levels:
//   1. AppErrorBoundary     — catches everything, always shows 911 contacts
//   2. FeatureErrorBoundary — one feature fails, rest of app stays alive
//   3. MapErrorBoundary     — map-specific, offers Google Maps fallback
//
// Plus two functional fallbacks:
//   GPSFallback    — permission denied / signal lost
//   OfflineFallback — shows stale-data warning inline

import { Component } from 'react';
import { captureException, setContext, breadcrumb } from '@services/monitoring';
import { T } from '@components/theme';

const S = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: '100dvh', maxWidth: 430,
    margin: '0 auto', background: T.bg, fontFamily: T.font,
  },
  center: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center',
    flex: 1, padding: '32px 20px', overflowY: 'auto',
  },
  icon:  { fontSize: 48, marginBottom: 12 },
  title: { fontWeight: 900, fontSize: 18, color: T.text, marginBottom: 8 },
  body:  { fontSize: 13, color: T.dim, lineHeight: 1.6, marginBottom: 20, maxWidth: 320 },
  btn: (color = T.amber) => ({
    padding: '12px 24px', borderRadius: 10,
    background: color, color: '#fff', border: 'none',
    fontSize: 14, fontWeight: 800, cursor: 'pointer',
    fontFamily: T.font, marginBottom: 8, width: '100%', maxWidth: 280,
  }),
  callBtn: {
    display: 'block', padding: '14px', borderRadius: 10,
    background: '#dc2626', color: '#fff', textDecoration: 'none',
    fontSize: 16, fontWeight: 900, fontFamily: T.font,
    textAlign: 'center', marginTop: 8, width: '100%', maxWidth: 280,
  },
  contact: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 12px', marginBottom: 6, borderRadius: 8,
    background: T.card, border: `1px solid ${T.bdr}`,
    textDecoration: 'none', color: T.text, fontSize: 13, fontWeight: 700,
    width: '100%', maxWidth: 320,
  },
  detail: {
    marginTop: 12, padding: '10px 12px', borderRadius: 8,
    background: T.bg2, border: `1px solid ${T.bdr}`,
    fontSize: 10, color: T.dim, fontFamily: 'monospace',
    textAlign: 'left', wordBreak: 'break-word', maxWidth: 320, width: '100%',
  },
};

const EMERGENCY_CONTACTS = [
  { label: 'Emergency — 911',     tel: '911' },
  { label: 'Non-Emergency Police', tel: '5046581000' },
  { label: 'Entergy Outages',      tel: '18004393932' },
  { label: 'Red Cross Louisiana',  tel: '18004733767' },
];

// ── 1. App-level — last line of defense ──────────────────────────
export class AppErrorBoundary extends Component {
  state = { error: null, errorInfo: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Report to Sentry with full component stack
    setContext('react', { componentStack: errorInfo?.componentStack?.substring(0, 2000) });
    captureException(error);
    if (import.meta.env.DEV) console.error('[AppErrorBoundary]', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={S.page}>
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '12px 16px', flexShrink: 0 }}>
          <div style={{ fontWeight: 900, fontSize: 14 }}>Eye on Storm — App Error</div>
          <div style={{ fontSize: 11, opacity: 0.8 }}>Emergency contacts below are still accessible</div>
        </div>

        <div style={S.center}>
          <div style={S.icon}>⚠️</div>
          <div style={S.title}>Something went wrong</div>
          <div style={S.body}>
            An unexpected error occurred. {navigator.onLine === false ? 'You appear to be offline — some features require a connection.' : 'Your emergency contacts are always accessible below.'}
          </div>

          <button onClick={this.handleReset} style={S.btn(T.amber)}>
            Reload App
          </button>

          <div style={{ width: '100%', maxWidth: 320, marginTop: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: T.dim, letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>
              Emergency Contacts
            </div>
            {EMERGENCY_CONTACTS.map((c) => (
              <a key={c.tel} href={`tel:${c.tel}`} style={S.contact}>
                <span>{c.label}</span>
                <span style={{ color: T.blue, fontWeight: 900 }}>Call</span>
              </a>
            ))}
            <a href="tel:911" style={S.callBtn}>📞 CALL 911</a>
          </div>

          {import.meta.env.DEV && this.state.error && (
            <div style={S.detail}>
              <strong>{this.state.error.toString()}</strong>
              {this.state.errorInfo?.componentStack && (
                <pre style={{ marginTop: 6, fontSize: 9, whiteSpace: 'pre-wrap' }}>
                  {this.state.errorInfo.componentStack.trim().split('\n').slice(0, 8).join('\n')}
                </pre>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }
}

// ── 2. Feature-level — one section fails, app stays alive ────────
export class FeatureErrorBoundary extends Component {
  state = { error: null, retries: 0 };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    breadcrumb(`Feature error: ${this.props.name}`, { error: error.message }, 'error');
    setContext('feature', { name: this.props.name, retries: this.state.retries });
    captureException(error);
    if (import.meta.env.DEV) {
      console.error(`[FeatureErrorBoundary:${this.props.name}]`, error);
    }
  }

  handleRetry = () => {
    this.setState((s) => ({ error: null, retries: s.retries + 1 }));
  };

  render() {
    if (!this.state.error) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div style={{ ...S.center, justifyContent: 'center' }}>
        <div style={S.icon}>🔧</div>
        <div style={S.title}>{this.props.name ?? 'Feature'} unavailable</div>
        <div style={S.body}>
          This section encountered an error. The rest of the app is still working.
          Try navigating away and back, or tap retry below.
        </div>
        {this.state.retries < 3 && (
          <button onClick={this.handleRetry} style={S.btn(T.amber)}>
            Try Again ({3 - this.state.retries} left)
          </button>
        )}
        {import.meta.env.DEV && (
          <div style={S.detail}>{this.state.error.toString()}</div>
        )}
      </div>
    );
  }
}

// ── 3. Map boundary — offers navigation fallback ─────────────────
export class MapErrorBoundary extends Component {
  state = { error: null };

  componentDidCatch(error) {
    import('@services/monitoring').then(({ captureMapError }) => captureMapError(error));
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, padding: 24, background: '#e8dfc8', textAlign: 'center' }}>
        <div style={S.icon}>🗺️</div>
        <div style={S.title}>Map unavailable</div>
        <div style={S.body}>
          The map could not load. This may be a network issue or missing tile cache.
          Emergency info is still available in the Feed.
        </div>
        <button onClick={() => this.setState({ error: null })} style={{ ...S.btn(T.blue), maxWidth: 220 }}>
          Retry Map
        </button>
        <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" style={{ fontSize: 13, color: T.blue, fontWeight: 700, marginTop: 8, textDecoration: 'none' }}>
          Open Google Maps instead
        </a>
      </div>
    );
  }
}

// ── GPS Permission Fallback (functional) ─────────────────────────
export function GPSFallback({ error, onRetry, onManualEntry }) {
  const denied = error?.toLowerCase().includes('denied') || error?.toLowerCase().includes('permission');

  return (
    <div style={{ padding: '16px', background: T.bg2, borderRadius: 12, border: `1px solid ${T.bdr}` }}>
      <div style={{ fontSize: 22, marginBottom: 8 }}>📍</div>
      <div style={{ fontWeight: 800, fontSize: 14, color: T.text, marginBottom: 4 }}>
        {denied ? 'Location access denied' : 'Could not get your location'}
      </div>
      <div style={{ fontSize: 12, color: T.dim, lineHeight: 1.5, marginBottom: 12 }}>
        {denied
          ? 'To enable: Settings > Privacy & Security > Location Services > Eye on Storm > While Using.'
          : error ?? 'GPS signal unavailable. Enter your address manually.'}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {onRetry && (
          <button onClick={onRetry} style={{ flex: 1, padding: '10px', borderRadius: 10, background: T.amber, color: '#fff', border: 'none', fontSize: 12, fontWeight: 800, cursor: 'pointer', fontFamily: T.font }}>
            Try Again
          </button>
        )}
        {onManualEntry && (
          <button onClick={onManualEntry} style={{ flex: 1, padding: '10px', borderRadius: 10, border: `1.5px solid ${T.bdr}`, background: T.card, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, color: T.text }}>
            Enter Address
          </button>
        )}
      </div>
    </div>
  );
}

// ── Offline section fallback (inline, not a crash) ───────────────
export function OfflineFallback({ feature, lastUpdated, onRetry, children }) {
  const ago = lastUpdated
    ? Math.round((Date.now() - lastUpdated) / 60000)
    : null;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: `${T.amber}15`, borderBottom: `1px solid ${T.amber}30`, flexShrink: 0 }}>
        <span style={{ fontSize: 16 }}>📡</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 12, color: T.amber }}>
            Offline — showing cached data
          </div>
          {ago !== null && (
            <div style={{ fontSize: 10, color: T.dim }}>Last updated {ago}m ago</div>
          )}
        </div>
        {onRetry && (
          <button onClick={onRetry} style={{ background: 'none', border: `1px solid ${T.amber}`, borderRadius: 8, padding: '3px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, color: T.amber }}>
            Retry
          </button>
        )}
      </div>
      {children}
    </>
  );
}
